import { Autocomplete, Box, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useMemo, useRef, useState } from "react";
import FileIcon from './FileIcon';
import { formatSize, getMimeType } from './file-utils';
import { highlightParts, isEmptyQuery, searchEntries } from './search';

const SUGGESTIONS = 8;
const SHOW_ALL = { showAll: true };

function parentLabel(path) {
    const index = path.lastIndexOf('/');
    return index <= 0 ? 'Drive saya' : path.slice(0, index);
}

function Highlighted({ name, query }) {
    return highlightParts(name, query).map((part, i) => (
        part.match ? <Box component="b" key={i} sx={{ color: 'primary.main' }}>{part.text}</Box> : <span key={i}>{part.text}</span>
    ));
}

// Kolom Cari. Saat mengetik muncul saran (file & folder paling cocok di
// seluruh drive); pilih saran untuk langsung membukanya, atau Enter untuk
// melihat semua hasil di tabel. "/" atau Ctrl+K dari mana saja = fokus ke sini.
//
// value / onChange: teks di kolom (dikendalikan App).
// onSubmit(teks): tampilkan semua hasil. onPick(entri): buka satu hasil.
// onClear: kolom dikosongkan lewat tombol X.
// version: berubah kalau daftar file berubah (saran dihitung ulang).
function SearchBar({ fileManager, value, onChange, onSubmit, onPick, onClear, version, placeholder = "Cari di Drive" }) {
    const inputRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value);

    // Saran dihitung sedikit setelah berhenti mengetik (akun besar punya
    // ratusan ribu file).
    useEffect(() => {
        const timer = setTimeout(() => setQuery(value), 120);
        return () => clearTimeout(timer);
    }, [value]);

    const found = useMemo(() => {
        if (!fileManager || isEmptyQuery(query)) return { results: [], total: 0 };
        return searchEntries(fileManager.searchIndex(), query, { limit: SUGGESTIONS });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileManager, query, version]);

    useEffect(() => {
        const onKeyDown = (event) => {
            const target = event.target;
            const typing = target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
            const ctrlK = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k';
            if (!ctrlK && (typing || event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey)) return;
            if (!inputRef.current) return;
            event.preventDefault();
            inputRef.current.focus();
            inputRef.current.select();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    const hasQuery = !isEmptyQuery(value);
    const options = found.total > 0 ? [...found.results, SHOW_ALL] : [];

    return <Autocomplete
        freeSolo
        disableClearable
        clearOnBlur={false}
        handleHomeEndKeys={false}
        open={open && hasQuery}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        filterOptions={(x) => x}
        value={null}
        inputValue={value}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.showAll ? value : option.name)}
        noOptionsText={query === value ? 'Tidak ada file atau folder yang cocok' : 'Mencari...'}
        onInputChange={(event, text, reason) => {
            // 'reset' = MUI mengisi kolom dengan nama saran yang dipilih: abaikan.
            if (reason !== 'reset') onChange(text);
        }}
        onChange={(event, option) => {
            setOpen(false);
            if (typeof option === 'string') {
                if (!isEmptyQuery(option)) onSubmit(option);
            } else if (option && option.showAll) {
                onSubmit(value);
            } else if (option) {
                onPick(option);
            }
        }}
        renderOption={(props, option) => (option.showAll ? (
            <Box component="li" {...props} key="__all" sx={{ gap: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <SearchIcon fontSize="small" color="action" />
                <Typography variant="body2">
                    Lihat semua {found.total} hasil <Typography component="span" variant="caption" color="text.secondary">(Enter)</Typography>
                </Typography>
            </Box>
        ) : (
            <Box component="li" {...props} key={option.path} sx={{ gap: 1.5 }}>
                <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', flexShrink: 0, color: 'text.secondary' }}>
                    <FileIcon mimeType={option.type === 'directory' ? 'inode/directory' : getMimeType(option.name)} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" noWrap title={option.name}><Highlighted name={option.name} query={query} /></Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div" title={parentLabel(option.path)}>
                        {parentLabel(option.path)}
                    </Typography>
                </Box>
                {option.type !== 'directory' && (
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{formatSize(option.node.size)}</Typography>
                )}
            </Box>
        ))}
        renderInput={(params) => (
            <TextField
                {...params}
                inputRef={inputRef}
                placeholder={placeholder}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '24px',
                        bgcolor: 'action.hover',
                        '& fieldset': { borderColor: 'transparent' },
                        '&:hover fieldset': { borderColor: 'transparent' },
                        '&.Mui-focused': { bgcolor: 'background.paper' },
                        '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                    },
                }}
                // Bukan kolom username: jangan diisi otomatis oleh password manager.
                inputProps={{
                    ...params.inputProps,
                    name: 'disbox-search',
                    autoComplete: 'off',
                    'data-lpignore': 'true',
                    'data-1p-ignore': 'true',
                    'aria-label': 'Cari file dan folder',
                }}
                InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                        <InputAdornment position="start" sx={{ ml: 0.5 }}>
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                    endAdornment: value ? (
                        <InputAdornment position="end">
                            <IconButton size="small" aria-label="Hapus pencarian" onClick={() => { onChange(''); if (onClear) onClear(); }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ) : (
                        <InputAdornment position="end" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                            <Box component="kbd" sx={{ px: 0.75, py: 0.1, fontSize: 12, fontFamily: 'inherit', color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>/</Box>
                        </InputAdornment>
                    ),
                }}
            />
        )}
    />
}

export default SearchBar;
